// TestimonialLinkPage.jsx — Public (no-auth) form for a client to
// submit a testimonial after their appointment was marked "attended".
// Reached via a unique token URL generated by admin: /testimonio/<token>
//
// Flow:
//   1. Fetch context (service + client name + used?/expired?).
//   2. Show star rating + textarea + submit.
//   3. On success, redirect to /testimonios so the client sees their
//      comment in the public list (they'll spot it easily since it's
//      the newest one and their name is preserved with the same
//      censoring rule used everywhere else).
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Send, Loader2, CheckCircle2, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useVoiceGuide } from "@/contexts/VoiceAssistantContext";
import { CountryCombobox } from "@/components/CountryCombobox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestimonialLinkPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(5);
  const [country, setCountry] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  useVoiceGuide("testi:form", "Gracias por compartir tu experiencia. Elige las estrellas que quieras darle a Cristopher, selecciona tu país y escribe con tus palabras cómo te fue y qué cambió para ti. Cuando termines, toca Enviar. Tu testimonio ayudará a otras personas.", !!context && !context?.used && !submitted && !loading);
  useVoiceGuide("testi:done", "Tu testimonio fue enviado. Muchas gracias por tu confianza y bendiciones para ti.", submitted);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/testimonial-link/${token}`);
        setContext(r.data);
        if (r.data?.country) setCountry(String(r.data.country).toUpperCase());
      } catch (e) {
        setError(e?.response?.data?.detail || "Enlace no válido");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (text.trim().length < 20) {
      toast.error("El testimonio debe tener al menos 20 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      if (!country) {
        toast.error("Selecciona tu país");
        setSubmitting(false);
        return;
      }
      await axios.post(`${API}/testimonial-link/${token}/submit`, {
        rating,
        text: text.trim(),
        lang: "es",
        country,
      });
      setSubmitted(true);
      toast.success("¡Gracias! Tu testimonio ya está publicado.");
      // Give the user a moment to see the success screen, then move to
      // /testimonios so they can spot their new comment at the top.
      setTimeout(() => navigate("/testimonios"), 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el testimonio");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <div className="text-6xl mb-3">🙁</div>
          <h1 className="font-serif text-2xl text-white mb-2">Enlace no disponible</h1>
          <p className="text-neutral-400 text-sm mb-6">{error}</p>
          <Link
            data-testid="testimonial-link-home"
            to="/testimonios"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37] hover:bg-[#d4af37]/20 text-sm font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Ver testimonios
          </Link>
        </div>
      </div>
    );
  }

  if (context?.used || submitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
          <h1 className="font-serif text-2xl text-white mb-2">¡Testimonio publicado!</h1>
          <p className="text-neutral-400 text-sm mb-6">
            Gracias por compartir tu experiencia. Tu comentario ya aparece junto a los demás testimonios.
          </p>
          <Link
            data-testid="testimonial-link-see"
            to="/testimonios"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#d4af37]/50 bg-[#d4af37]/10 text-[#d4af37] hover:bg-[#d4af37]/20 text-sm font-semibold"
          >
            Ver testimonios <Sparkles className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white flex items-center justify-center p-4"
      style={{
        background:
          "radial-gradient(circle at 15% 10%, rgba(120,40,160,0.32) 0%, transparent 45%), radial-gradient(circle at 85% 90%, rgba(212,175,55,0.15) 0%, transparent 45%), linear-gradient(180deg, #0b0612 0%, #05010a 100%)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-lg rounded-2xl border border-[#d4af37]/30 bg-black/60 backdrop-blur-md shadow-[0_0_60px_rgba(212,175,55,0.18)] p-6 sm:p-8"
      >
        <div className="text-[10px] uppercase tracking-[0.35em] text-[#d4af37]/80 mb-2 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Testimonio · CrisAngel Astral
        </div>
        <h1 className="font-serif text-2xl sm:text-3xl text-white mb-1">
          Hola {context?.user_name?.split(" ")[0] || "amig@"}
        </h1>
        <p className="text-neutral-400 text-sm mb-6">
          Comparte cómo te fue en tu sesión de <span className="text-[#d4af37]">{context?.service_name}</span>. Tu testimonio se publicará junto a los demás en la sección de testimonios.
        </p>

        <form onSubmit={submit} className="space-y-4">
          {/* Rating stars */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1.5">Tu calificación</label>
            <div className="flex items-center gap-1" data-testid="testimonial-rating-row">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`testimonial-star-${n}`}
                  onClick={() => setRating(n)}
                  className="transition transform hover:scale-110"
                  aria-label={`${n} estrella${n > 1 ? "s" : ""}`}
                >
                  <Star
                    className={`w-8 h-8 ${n <= rating ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-700"}`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Country */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1.5">Tu país</label>
            <CountryCombobox value={country} onChange={setCountry} testId="testimonial-country" />
            <div className="text-[10px] text-neutral-500 mt-1">Se mostrará la bandera de tu país junto a tu testimonio.</div>
          </div>

          {/* Testimonial text */}
          <div>
            <label htmlFor="tst-text" className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1.5">
              Tu experiencia ({text.trim().length}/1200)
            </label>
            <textarea
              id="tst-text"
              data-testid="testimonial-textarea"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 1200))}
              rows={6}
              placeholder="Cuenta cómo te sentiste durante la sesión, qué acertó el maestro y cómo cambió tu situación después de la lectura o del trabajo."
              className="w-full bg-black/60 border border-white/10 focus:border-[#d4af37]/60 outline-none rounded-xl px-3 py-2.5 text-sm placeholder-neutral-600 text-white resize-none"
              minLength={20}
              required
            />
            <div className="text-[10px] text-neutral-500 mt-1">Mínimo 20 caracteres. Tu nombre completo se censurará automáticamente en la publicación pública.</div>
          </div>

          <button
            type="submit"
            data-testid="testimonial-submit"
            disabled={submitting || text.trim().length < 20}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full bg-gradient-to-r from-[#d4af37] via-[#f3d878] to-[#d4af37] text-black font-bold uppercase tracking-widest text-xs hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? "Enviando…" : "Publicar mi testimonio"}
          </button>
        </form>

        <div className="mt-6 text-[10px] text-neutral-600 text-center">
          Este enlace es único y sólo puede usarse una vez. Bendiciones ✨
        </div>
      </motion.div>
    </div>
  );
}
